import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import {
	Customer as CustomerEntity,
	JobSummary,
	Railroad,
	FieldWorker,
} from 'src/shared/api';
import * as CustomerDetailActions from './customer-detail.actions';

export const customerDetailFeatureKey = 'customer-detail';

interface Customer extends CustomerEntity {
	jobs?: JobSummary[];
}

export interface CustomerDetailsState extends EntityState<Customer> {
	selectedCustomerId?: number;
	error?: any;
	isLoading?: boolean;
	shouldStayOnPage?: boolean;
	railroads?: Railroad[];
	jobs?: JobSummary[];
	rwics?: FieldWorker[];
	isSaving?: boolean;
}

export const customerDetailAdapter: EntityAdapter<Customer> = createEntityAdapter<
	Customer
>();

export interface CustomerDetailPartialState {
	readonly [customerDetailFeatureKey]: CustomerDetailsState;
}

export const initialState: CustomerDetailsState = customerDetailAdapter.getInitialState(
	{
		isLoading: true,
		isLoadingLogo: true,
		isSaving: false,
		railroads: [],
		jobs: [],
		rwics: [],
		// additional entity state properties
	}
);

const customersReducer = createReducer(
	initialState,
	on(
		CustomerDetailActions.customerLoadedFromStore,
		CustomerDetailActions.newCustomerContinued,
		(state) => ({ ...state, isLoading: false })
	),
	on(CustomerDetailActions.saveRequested, (state, { shouldStayOnPage }) => ({
		...state,
		isSaving: true,
		shouldStayOnPage,
	})),
	on(CustomerDetailActions.saveRequestedWithInvalidForm, (state) => ({
		...state,
		error: 'Form Invalid',
	})),
	on(CustomerDetailActions.formErrorCleared, (state) => ({
		...state,
		error: undefined,
	})),
	on(CustomerDetailActions.newCustomerSaved, (state) =>
		customerDetailAdapter.removeOne(0, state)
	),
	on(CustomerDetailActions.newCustomerSaved, (state, { customer }) =>
		customerDetailAdapter.addOne(customer, { ...state, isSaving: false })
	),
	on(CustomerDetailActions.customerSaved, (state, { customer }) =>
		customerDetailAdapter.updateOne(
			{
				id: customer.id,
				changes: {
					...customer,
				},
			},
			{ ...state, isSaving: false }
		)
	),
	on(CustomerDetailActions.customerSelected, (state, { customerId }) => ({
		...state,
		selectedCustomerId: customerId,
	})),
	on(CustomerDetailActions.railroadsLoaded, (state, { railroads }) => ({
		...state,
		railroads,
	})),
	on(CustomerDetailActions.jobsLoaded, (state, { jobs }) => ({
		...state,
		jobs,
	})),
	on(CustomerDetailActions.rwicsLoaded, (state, { rwics }) => ({
		...state,
		rwics,
	})),
	on(CustomerDetailActions.customerLoadedFromApi, (state, { customer }) =>
		customerDetailAdapter.upsertOne(customer, {
			...state,
			isLoading: false,
		})
	),
	on(CustomerDetailActions.errorSavingCustomer, (state, { error }) => ({
		...state,
		error: error,
		isSaving: false,
	})),
	on(CustomerDetailActions.newCustomerStarted, (state) =>
		customerDetailAdapter.addOne({ id: 0 }, { ...state, isLoading: false })
	),
	on(CustomerDetailActions.customerDetailDestroyed, (state) => ({
		...state,
		isLoading: true,
		isLoadingLogo: true,
		error: undefined,
		railroads: [],
		jobs: [],
		rwics: [],
	}))
);

export function reducer(
	state: CustomerDetailsState | undefined,
	action: Action
) {
	return customersReducer(state, action);
}
